import axios from "axios";
import CryptoJS from "crypto-js";
import { jwtDecode } from "jwt-decode"; // Install: npm install jwt-decode
import { getAccessToken } from "../auth/authEffect";
import { BASE_URL } from "../baseUrl/baseURL";

const SECRET_KEY = "843567893696976453275974432697R634976";

const authInstance = axios.create({
  baseURL: BASE_URL,
  headers: { "Content-Type": "application/json" },
});

// ======================= TOKEN VALIDATION =======================
function isTokenExpired(token) {
  try {
    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000;

    // Check if token is expired (with 10 second buffer)
    return decoded.exp < currentTime + 10;
  } catch (error) {
    console.error("Token decode error:", error);
    return true; // Treat invalid tokens as expired
  }
}

function getTokenExpiryTime(token) {
  try {
    const decoded = jwtDecode(token);
    return decoded.exp * 1000; // Convert to milliseconds
  } catch (error) {
    return null;
  }
}

// ======================= DYNAMIC TOKEN REFRESH =======================
let tokenRefreshTimer = null;

function startTokenRefreshTimer(accessToken) {
  if (tokenRefreshTimer) {
    clearInterval(tokenRefreshTimer);
  }

  const expiryTime = getTokenExpiryTime(accessToken);
  if (!expiryTime) return;

  const currentTime = Date.now();
  const timeUntilExpiry = expiryTime - currentTime;

  // Refresh token 1 minute before expiry (or 80% of lifetime)
  const refreshTime = Math.max(timeUntilExpiry * 0.8, 60000);

  tokenRefreshTimer = setTimeout(async () => {
    try {
      await handleTokenRefresh();
    } catch (error) {
      console.error("Automatic token refresh failed:", error);
      clearAuthAndRedirect();
    }
  }, refreshTime);
}

function stopTokenRefreshTimer() {
  if (tokenRefreshTimer) {
    clearTimeout(tokenRefreshTimer);
    tokenRefreshTimer = null;
  }
}

function clearAuthAndRedirect() {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  stopTokenRefreshTimer();
  window.location.href = "/";
}

// ======================= REQUEST INTERCEPTOR =======================
authInstance.interceptors.request.use(
  (config) => {
    // Skip token checks for public endpoints
    if (config.url.includes("/login") || config.url.includes("/signup")) {
      return config;
    }

    const encryptedAccessToken = localStorage.getItem("accessToken");
    const refreshToken = localStorage.getItem("refreshToken");

    if (!encryptedAccessToken || !refreshToken) {
      clearAuthAndRedirect();
      return Promise.reject(new Error("No authentication tokens"));
    }

    try {
      const bytes = CryptoJS.AES.decrypt(encryptedAccessToken, SECRET_KEY);
      const decryptedAccessToken = bytes.toString(CryptoJS.enc.Utf8);

      // ✅ VALIDATE TOKEN EXPIRY
      if (!decryptedAccessToken || isTokenExpired(decryptedAccessToken)) {
        console.warn("Access token expired or invalid");
        clearAuthAndRedirect();
        return Promise.reject(new Error("Token expired"));
      }

      config.headers["Authorization"] = `Bearer ${decryptedAccessToken}`;
    } catch (err) {
      console.error("Token decryption error:", err);
      clearAuthAndRedirect();
      return Promise.reject(err);
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ======================= TOKEN REFRESH =======================
let tokenRefreshPromise = null;

async function handleTokenRefresh() {
  if (!tokenRefreshPromise) {
    const encryptedRefreshToken = localStorage.getItem("refreshToken");
    if (!encryptedRefreshToken) {
      return Promise.reject("No refresh token found");
    }

    const bytes = CryptoJS.AES.decrypt(encryptedRefreshToken, SECRET_KEY);
    const decryptedRefreshToken = bytes.toString(CryptoJS.enc.Utf8);

    // ✅ CHECK REFRESH TOKEN EXPIRY
    if (isTokenExpired(decryptedRefreshToken)) {
      clearAuthAndRedirect();
      return Promise.reject("Refresh token expired");
    }

    tokenRefreshPromise = getAccessToken({ token: decryptedRefreshToken })
      .then((response) => {
        const { accessToken, refreshToken } = response.data;

        const encryptedAccess = CryptoJS.AES.encrypt(
          accessToken,
          SECRET_KEY
        ).toString();
        const encryptedRefresh = CryptoJS.AES.encrypt(
          refreshToken,
          SECRET_KEY
        ).toString();

        localStorage.setItem("accessToken", encryptedAccess);
        localStorage.setItem("refreshToken", encryptedRefresh);

        // ✅ START NEW TIMER BASED ON NEW TOKEN EXPIRY
        startTokenRefreshTimer(accessToken);

        return accessToken;
      })
      .catch((error) => {
        clearAuthAndRedirect();
        throw error;
      })
      .finally(() => {
        tokenRefreshPromise = null;
      });
  }
  return tokenRefreshPromise;
}

// ======================= RESPONSE INTERCEPTOR =======================
authInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response) {
      const { status } = error.response;

      // Unauthorized - clear everything
      if (status === 401) {
        clearAuthAndRedirect();
        return Promise.reject(error);
      }

      // Forbidden - try to refresh token
      if (status === 403 && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          const newAccessToken = await handleTokenRefresh();
          originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
          return authInstance(originalRequest);
        } catch (refreshError) {
          clearAuthAndRedirect();
          return Promise.reject(refreshError);
        }
      }
    }
    return Promise.reject(error);
  }
);

export { startTokenRefreshTimer, stopTokenRefreshTimer };
export default authInstance;